import { useMemo, useCallback } from 'react';
import { Save, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AvailableEmployeesPanel } from './available-employees-panel';
import { SelectedPanel } from './selected-panel';
import type {
  EmployeePickerProps,
  SelectedEmployee,
} from './types';
import { createEmptySelection } from './types';

// ============================================================================
// Component
// ============================================================================

/**
 * Employee picker component for policy assignments.
 *
 * Shows two panels:
 * - Left: Search/filter employees and select them
 * - Right: Selected employees list
 *
 * Includes "Select All" button to quickly assign all employees.
 */
export function EmployeePicker({
  value,
  onChange,
  disabled = false,
  showSaveButton = true,
  saveButtonText = 'Save Assignments',
  onSave,
  isSaving = false,
  hasChanges = false,
  onSelectAll,
  isLoadingAll = false,
  onForceRemoveLocked,
}: EmployeePickerProps) {
  // Calculate excluded IDs for the available panel
  const excludeEmployeeIds = useMemo(
    () => new Set(value.employees.map((e) => e.id)),
    [value.employees]
  );

  // Total selected count
  const totalCount = value.employees.length;

  // Handle adding employees
  const handleAddEmployees = useCallback(
    (employees: SelectedEmployee[]) => {
      onChange({
        ...value,
        employees: [...value.employees, ...employees],
      });
    },
    [value, onChange]
  );

  // Handle removing employee. Locked entries (e.g. via employment-type) cannot
  // be removed from this picker — they unlock by toggling their source rule.
  const handleRemoveEmployee = useCallback(
    (employeeId: string) => {
      onChange({
        ...value,
        employees: value.employees.filter(
          (e) => e.id !== employeeId || e.locked === true
        ),
      });
    },
    [value, onChange]
  );

  // Handle clear all — preserve locked entries.
  const handleClearAll = useCallback(() => {
    const locked = value.employees.filter((e) => e.locked === true);
    if (locked.length === 0) {
      onChange(createEmptySelection());
    } else {
      onChange({ ...value, employees: locked });
    }
  }, [value, onChange]);

  return (
    <div className="space-y-4">
      {/* Header with Select All button */}
      <div className="flex items-center justify-between p-4 rounded-xl border bg-card">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-medium text-sm">Assigned Employees</div>
            <div className="text-xs text-muted-foreground">
              {totalCount === 0
                ? 'No employees assigned yet'
                : `${totalCount} employee${totalCount !== 1 ? 's' : ''} assigned`}
            </div>
          </div>
          {totalCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {totalCount}
            </Badge>
          )}
        </div>

        {/* Select All Button */}
        {onSelectAll && (
          <Button
            variant="outline"
            size="sm"
            onClick={onSelectAll}
            disabled={disabled || isSaving || isLoadingAll}
            className="gap-2"
          >
            {isLoadingAll ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Users className="h-4 w-4" />
                Select All Employees
              </>
            )}
          </Button>
        )}
      </div>

      {/* Employee Selection Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[420px]">
        {/* Left: Available Employees */}
        <AvailableEmployeesPanel
          excludeEmployeeIds={excludeEmployeeIds}
          onAddEmployees={handleAddEmployees}
          disabled={disabled || isSaving}
        />

        {/* Right: Selected Employees */}
        <SelectedPanel
          selection={value}
          onRemoveEmployee={handleRemoveEmployee}
          onClearAll={handleClearAll}
          onForceRemoveLocked={onForceRemoveLocked}
          disabled={disabled || isSaving}
        />
      </div>

      {/* Save Button */}
      {showSaveButton && onSave && (
        <div className="flex items-center justify-end gap-4 pt-4 border-t">
          {hasChanges && (
            <span className="text-sm text-muted-foreground">
              Unsaved changes
            </span>
          )}
          <Button
            onClick={onSave}
            disabled={disabled || isSaving || !hasChanges}
            className="min-w-[140px]"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {saveButtonText}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
